import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Tooltip,
  Modal,
  Input as AntInput,
  message,
  Badge,
  Drawer,
  Alert,
  Divider,
  List,
} from "antd";
import { useNavigate } from "react-router-dom";
import type { RangePickerProps } from "antd/es/date-picker";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ContainerOutlined,
  HomeOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import styled from "@emotion/styled";
import {
  Booking,
  PaginationParams,
  Company,
  Port,
  TransportType,
  BookingStatus,
  bookingStatusLabels,
  Container,
  Agent,
  BookingType,
  InvoiceInfo,
  InvoiceLine,
} from "../../types/booking";
import {
  fetchBookings,
  updateBookingStatus,
  fetchAgents,
  linkMultipleBookingsToContainer,
  bulkUpdateBookingStatus,
} from "../../services/bookingService";
import { getStatusColor, formatDate } from "../../utils/bookingUtils";
import { trackFeatureUsage } from "../../lib/posthog";
import { supabase } from "../../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { fetchContainers } from "../../services/containerService";
import { generateAndDownloadPackingSlip } from "../../services/packingSlipService";
import { fetchMasterContainersForFilter } from "../../services/masterContainerService";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Type cast supabase to avoid type errors
const typedSupabase = supabase as unknown as SupabaseClient;

// Define styled components
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

interface BookingsListProps {
  isPendingList?: boolean;
  onViewDetails: (booking: Booking) => void;
  onRefresh: () => void;
  lastUpdate?: Date | null;
  statusFilter?: string;
  resetFilters?: number | null;
}

const BookingsList: React.FC<BookingsListProps> = ({
  isPendingList = false,
  onViewDetails,
  onRefresh,
  lastUpdate = null,
  statusFilter: externalStatusFilter,
  resetFilters = null,
}) => {
  // Navigation
  const navigate = useNavigate();

  // State
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(
    isPendingList ? BookingStatus.SUBMITTED : "all"
  );
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<
    "ascend" | "descend" | undefined
  >();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [companyFilter, setCompanyFilter] = useState<string | null>(null);
  const [transportTypeFilter, setTransportTypeFilter] = useState<string | null>(
    null
  );
  const [consolidationFilter, setConsolidationFilter] = useState<
    boolean | null
  >(null);
  const [containerFilter, setContainerFilter] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [containers, setContainers] = useState<Container[]>([]);
  // Add state to track bookings being processed
  const [processingBookingIds, setProcessingBookingIds] = useState<number[]>(
    []
  );
  // State for tracking bookings with pending tasks
  const [bookingsWithPendingTasks, setBookingsWithPendingTasks] = useState<
    Set<number>
  >(new Set());
  // Add state for agentFilter and agents
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [bookingTypeFilter, setBookingTypeFilter] = useState<string | null>(
    null
  );
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [masterContainerFilter, setMasterContainerFilter] = useState<
    string | null
  >(null);
  const [masterContainers, setMasterContainers] = useState<
    Array<{ id: number; reference: string | null }>
  >([]);
  const [invoiceFilter, setInvoiceFilter] = useState<string | null>(null);
  const [deliveryFilter, setDeliveryFilter] = useState<string | null>(null);
  // State for multi-selection and bulk operations
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
  const [bulkActionDrawerVisible, setBulkActionDrawerVisible] = useState(false);
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(
    null
  );
  const [processingBulkAction, setProcessingBulkAction] = useState(false);

  // State for existing bookings in the selected container
  const [existingContainerBookings, setExistingContainerBookings] = useState<
    Booking[]
  >([]);
  const [loadingContainerBookings, setLoadingContainerBookings] =
    useState(false);

  // State for bulk status change
  const [bulkStatusChangeVisible, setBulkStatusChangeVisible] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState<string | null>(
    null
  );
  const [processingBulkStatus, setProcessingBulkStatus] = useState(false);

  // Update status filter when external status filter prop changes
  useEffect(() => {
    if (externalStatusFilter && externalStatusFilter !== "all") {
      setStatusFilter(externalStatusFilter);
    } else if (isPendingList) {
      setStatusFilter(BookingStatus.SUBMITTED);
    } else {
      setStatusFilter("all");
    }
  }, [externalStatusFilter, isPendingList]);

  // Add effect to handle filter reset
  useEffect(() => {
    if (resetFilters) {
      // Reset all filters except status filter (which is handled by externalStatusFilter)
      setSearchText("");
      setSearchQuery("");
      setDateRange(null);
      setCompanyFilter(null);
      setTransportTypeFilter(null);
      setConsolidationFilter(null);
      setContainerFilter(null);
      setAgentFilter(null);
      setBookingTypeFilter(null);
      setMasterContainerFilter(null);
      setInvoiceFilter(null);
      setDeliveryFilter(null);

      // Reset the pagination to first page
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      // Manually reload bookings with reset filters but preserve status filter
      loadBookings({
        page: 1,
        searchQuery: "",
        dateRange: null,
        companyId: null,
        transportTypeId: null,
        isConsolidation: null,
        containerId: null,
        agentId: null,
        bookingTypeId: null,
        masterContainerId: null,
        hasInvoice: null,
        deliveryType: null,
      });
    }
  }, [resetFilters]);

  // Initial data fetch
  useEffect(() => {
    loadBookings();
    fetchFilterOptions();
  }, [
    isPendingList,
    statusFilter,
    sortField,
    sortOrder,
    companyFilter,
    transportTypeFilter,
    consolidationFilter,
    containerFilter,
    agentFilter,
    bookingTypeFilter,
    masterContainerFilter,
    invoiceFilter,
    deliveryFilter,
  ]);

  // Refresh data when lastUpdate changes (for real-time updates)
  useEffect(() => {
    if (lastUpdate) {
      console.log(
        "BookingsList: lastUpdate changed, reloading bookings at",
        lastUpdate.toISOString()
      );
      // Force refresh of bookings with current filters
      loadBookings();
    }
  }, [lastUpdate]);

  // Fetch filter options (companies, transport types, containers, agents, booking types)
  const fetchFilterOptions = async () => {
    try {
      // Fetch companies for the filter
      const { data: companiesData, error: companiesError } = await typedSupabase
        .from("company")
        .select("id, name")
        .order("name");

      if (!companiesError && companiesData) {
        setCompanies(companiesData);
      }

      // Fetch transport types for the filter
      const { data: transportTypesData, error: transportTypesError } =
        await typedSupabase
          .from("booking_transport_types")
          .select("id, name, abbreviation")
          .order("name");

      if (!transportTypesError && transportTypesData) {
        setTransportTypes(transportTypesData);
      }

      // Fetch containers for the filter
      const containersResult = await fetchContainers();
      if (!containersResult.error) {
        setContainers(containersResult.data || []);
      }

      // Fetch agents for the filter
      const agentsResult = await fetchAgents();
      if (!agentsResult.error) {
        setAgents(agentsResult.data || []);
      }

      // Fetch booking types for the filter
      const { data: bookingTypesData, error: bookingTypesError } =
        await typedSupabase
          .from("bookings_type")
          .select("id, name")
          .order("name");

      if (!bookingTypesError && bookingTypesData) {
        setBookingTypes(bookingTypesData);
      }

      // Fetch master containers for the filter
      const masterContainersResult = await fetchMasterContainersForFilter();
      if (!masterContainersResult.error) {
        setMasterContainers(masterContainersResult.data || []);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Fetch bookings data
  const loadBookings = async (params?: Partial<PaginationParams>) => {
    try {
      console.log(
        "BookingsList: Loading bookings with params:",
        params || "default params"
      );
      setLoading(true);

      const paginationParams: PaginationParams = {
        page: params?.page || pagination.current,
        pageSize: params?.pageSize || pagination.pageSize,
        searchQuery: params?.searchQuery || searchQuery,
        status:
          params?.status || statusFilter === "all" ? undefined : statusFilter,
        dateRange: params?.dateRange || dateRange,
        sortField: params?.sortField || sortField,
        sortOrder: params?.sortOrder || sortOrder,
        companyId: params?.companyId || companyFilter,
        transportTypeId: params?.transportTypeId || transportTypeFilter,
        isConsolidation: params?.isConsolidation ?? consolidationFilter,
        containerId: params?.containerId || containerFilter,
        agentId: params?.agentId || agentFilter,
        bookingTypeId: params?.bookingTypeId || bookingTypeFilter,
        masterContainerId: params?.masterContainerId || masterContainerFilter,
        hasInvoice:
          params?.hasInvoice ??
          (invoiceFilter === "with_invoice"
            ? true
            : invoiceFilter === "no_invoice"
            ? false
            : null),
        deliveryType:
          params?.deliveryType ??
          (deliveryFilter === "storage"
            ? "storage"
            : deliveryFilter === "delivery"
            ? "delivery"
            : null),
      };

      console.log(
        "BookingsList: Fetching with status filter:",
        paginationParams.status
      );
      const result = await fetchBookings(paginationParams);

      if (result.error) {
        console.error("BookingsList: Error fetching bookings:", result.error);
        message.error("Failed to fetch bookings");
        return;
      }

      const bookingsData = result.data || [];
      console.log(
        `BookingsList: Fetched ${bookingsData.length} bookings:`,
        bookingsData.map((b) => b.id)
      );
      setBookings(bookingsData);
      setPagination({
        ...pagination,
        current: paginationParams.page,
        pageSize: paginationParams.pageSize,
        total: result.count || 0,
      });

      // Check for pending tasks for each booking
      if (bookingsData.length > 0) {
        checkPendingTasksForBookings(bookingsData);
      }

      // Track usage
      trackFeatureUsage(
        isPendingList ? "admin_pending_bookings_view" : "admin_bookings_view"
      );
    } catch (error) {
      console.error("Error loading bookings:", error);
      message.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  // Check for pending tasks for a list of bookings
  const checkPendingTasksForBookings = async (bookingsList: Booking[]) => {
    try {
      const bookingIds = bookingsList.map((booking) => booking.id);

      // Query tasks for all these bookings
      const { data, error } = await typedSupabase
        .from("tasks")
        .select("booking_id, completed")
        .in("booking_id", bookingIds)
        .eq("completed", false);

      if (error) {
        console.error("Error fetching pending tasks:", error);
        return;
      }

      if (data) {
        // Create a set of booking IDs that have pending tasks
        const pendingTaskBookingIds = new Set(
          data.map((task: { booking_id: number }) => Number(task.booking_id))
        );

        setBookingsWithPendingTasks(pendingTaskBookingIds as Set<number>);
      }
    } catch (error) {
      console.error("Error checking pending tasks:", error);
    }
  };

  // Handle search input change
  const handleSearch = (value: string) => {
    setSearchText(value);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout to debounce the search
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      loadBookings({
        page: 1,
        searchQuery: value,
      });
    }, 500); // 500ms debounce

    setSearchTimeout(timeout);
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    // Always default to "all" if value is null or undefined
    const safeValue = value || "all";
    setStatusFilter(safeValue);
    loadBookings({
      page: 1,
      status: safeValue,
    });
  };

  // Handle date range change
  const handleDateRangeChange: RangePickerProps["onChange"] = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const dateRangeValue: [string, string] = [
        dates[0].format("YYYY-MM-DD"),
        dates[1].format("YYYY-MM-DD"),
      ];
      setDateRange(dateRangeValue);
      loadBookings({
        page: 1,
        dateRange: dateRangeValue,
      });
    } else {
      setDateRange(null);
      loadBookings({
        page: 1,
        dateRange: undefined,
      });
    }
  };

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Booking> | SorterResult<Booking>[]
  ) => {
    // Handle sorting
    let newSortField: string | undefined;
    let newSortOrder: "ascend" | "descend" | undefined;

    if (!Array.isArray(sorter)) {
      newSortField = sorter.field as string;
      newSortOrder = sorter.order as "ascend" | "descend" | undefined;
    }

    // Set sort state for next query
    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Handle pagination
    loadBookings({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortField: newSortField,
      sortOrder: newSortOrder,
    });
  };

  // Handle booking approval
  const handleApproveBooking = async (booking: Booking) => {
    try {
      setLoading(true);
      // Add booking ID to processing list
      setProcessingBookingIds((prev) => [...prev, booking.id]);

      const result = await updateBookingStatus(
        booking.id,
        BookingStatus.APPROVED
      );

      if (result.error) {
        message.error("Failed to approve booking");
        return;
      }

      message.success(`Booking #${booking.id} approved successfully`);
      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_booking_approved");
    } catch (error) {
      console.error("Error approving booking:", error);
      message.error("Failed to approve booking");
    } finally {
      setLoading(false);
      // Remove booking ID from processing list after a delay to prevent immediate row click
      setTimeout(() => {
        setProcessingBookingIds((prev) =>
          prev.filter((id) => id !== booking.id)
        );
      }, 500);
    }
  };

  // Handle booking rejection
  const handleRejectBooking = async (
    booking: Booking,
    rejectionReason: string
  ) => {
    try {
      setLoading(true);
      // Add booking ID to processing list
      setProcessingBookingIds((prev) => [...prev, booking.id]);

      const result = await updateBookingStatus(
        booking.id,
        BookingStatus.REJECTED,
        {
          rejection_reason: rejectionReason,
        }
      );

      if (result.error) {
        message.error("Failed to reject booking");
        return;
      }

      message.success(`Booking #${booking.id} rejected successfully`);
      loadBookings();
      onRefresh();
      trackFeatureUsage("admin_booking_rejected");
    } catch (error) {
      console.error("Error rejecting booking:", error);
      message.error("Failed to reject booking");
    } finally {
      setLoading(false);
      // Remove booking ID from processing list after a delay to prevent immediate row click
      setTimeout(() => {
        setProcessingBookingIds((prev) =>
          prev.filter((id) => id !== booking.id)
        );
      }, 500);
    }
  };

  // Handle packing list generation and download
  const handleDownloadPackingSlip = async (booking: Booking) => {
    try {
      await generateAndDownloadPackingSlip(booking);
    } catch (error) {
      console.error("Error generating packing list:", error);
      message.error("Failed to generate packing list");
    }
  };

  // Handle company filter change
  const handleCompanyFilterChange = (value: string | null) => {
    setCompanyFilter(value);
    loadBookings({
      page: 1,
      companyId: value,
    });
  };

  // Handle transport type filter change
  const handleTransportTypeFilterChange = (value: string | null) => {
    setTransportTypeFilter(value);
    loadBookings({
      page: 1,
      transportTypeId: value,
    });
  };

  // Handle consolidation filter change
  const handleConsolidationFilterChange = (value: boolean | null) => {
    setConsolidationFilter(value);
    loadBookings({
      page: 1,
      isConsolidation: value,
    });
  };

  // Handle container filter change
  const handleContainerFilterChange = (value: string | null) => {
    setContainerFilter(value);
    setPagination({ ...pagination, current: 1 });
    loadBookings({
      page: 1,
      containerId: value,
    });
  };

  // Handle agent filter change
  const handleAgentFilterChange = (value: string | null) => {
    setAgentFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadBookings({
      page: 1,
      agentId: value,
    });
  };

  // Handle booking type filter change
  const handleBookingTypeFilterChange = (value: string | null) => {
    setBookingTypeFilter(value);
    loadBookings({
      page: 1,
      bookingTypeId: value,
    });
  };

  // Handle master container filter change
  const handleMasterContainerFilterChange = (value: string | null) => {
    setMasterContainerFilter(value);
    loadBookings({
      page: 1,
      masterContainerId: value,
    });
  };

  // Handle invoice filter change
  const handleInvoiceFilterChange = (value: string | null) => {
    setInvoiceFilter(value);
    loadBookings({
      page: 1,
      hasInvoice:
        value === "with_invoice" ? true : value === "no_invoice" ? false : null,
    });
  };

  const handleDeliveryFilterChange = (value: string | null) => {
    setDeliveryFilter(value);
    loadBookings({
      page: 1,
      deliveryType:
        value === "storage"
          ? "storage"
          : value === "delivery"
          ? "delivery"
          : null,
    });
  };

  const handleBulkStatusChange = async () => {
    if (!selectedBulkStatus || selectedBookingIds.length === 0) {
      message.error("Please select a status and at least one booking");
      return;
    }

    setProcessingBulkStatus(true);

    try {
      const result = await bulkUpdateBookingStatus(
        selectedBookingIds,
        selectedBulkStatus as BookingStatus
      );

      if (result.success) {
        message.success(
          `Successfully updated ${selectedBookingIds.length} booking(s) to ${selectedBulkStatus}`
        );

        // Clear selections and close modal
        setSelectedBookingIds([]);
        setBulkStatusChangeVisible(false);
        setSelectedBulkStatus(null);

        // Reload bookings to reflect changes
        loadBookings();

        // Track the bulk status change event
        trackFeatureUsage("bulk_status_change", {
          booking_count: selectedBookingIds.length,
          new_status: selectedBulkStatus,
        });
      } else {
        throw new Error("Bulk status update failed");
      }
    } catch (error) {
      console.error("Error bulk updating booking statuses:", error);
      message.error("Failed to update booking statuses");
    } finally {
      setProcessingBulkStatus(false);
    }
  };

  // Generate a formatted booking reference
  const getFormattedBookingReference = (booking: Booking) => {
    // Get transport type abbreviation
    const transportInfo = booking.transport_type_info;
    let transportAbbr = "";

    if (transportInfo) {
      if (Array.isArray(transportInfo) && transportInfo.length > 0) {
        transportAbbr = transportInfo[0].abbreviation || "";
      } else if (typeof transportInfo === "object") {
        transportAbbr = (transportInfo as TransportType).abbreviation || "";
      }
    }

    // Format: EFF<booking_id><transport_type.abbreviation>
    return `EFF${booking.id}${transportAbbr}`;
  };

  // Handle row selection change
  const rowSelection = {
    selectedRowKeys: selectedBookingIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedBookingIds(selectedRowKeys as number[]);
    },
  };

  // Handle adding multiple bookings to a container
  const handleAddToContainer = async () => {
    if (!selectedContainerId || selectedBookingIds.length === 0) {
      message.error("Please select a container and at least one booking");
      return;
    }

    try {
      setProcessingBulkAction(true);
      const containerIdNumber = parseInt(selectedContainerId);

      const result = await linkMultipleBookingsToContainer(
        selectedBookingIds,
        containerIdNumber
      );

      if (result.error) {
        message.error("Failed to add bookings to container");
        return;
      }

      message.success(
        `${selectedBookingIds.length} bookings added to container successfully`
      );

      // Reset selection and refresh
      setSelectedBookingIds([]);
      setBulkActionDrawerVisible(false);
      setSelectedContainerId(null);

      // Refresh the bookings list
      loadBookings();
      onRefresh();

      trackFeatureUsage("admin_bulk_add_to_container", {
        count: selectedBookingIds.length,
      });
    } catch (error) {
      console.error("Error performing bulk add to container:", error);
      message.error("Failed to process bulk action");
    } finally {
      setProcessingBulkAction(false);
    }
  };

  // Fetch bookings already in the selected container when container selection changes
  useEffect(() => {
    const fetchExistingBookings = async () => {
      if (!selectedContainerId || !bulkActionDrawerVisible) {
        return;
      }

      try {
        setLoadingContainerBookings(true);
        const result = await fetchBookings({
          containerId: selectedContainerId,
        });

        if (!result.error) {
          // Filter out bookings that are already selected
          const uniqueBookings = result.data.filter(
            (booking) => !selectedBookingIds.includes(booking.id)
          );
          setExistingContainerBookings(uniqueBookings);
        }
      } catch (error) {
        console.error("Error fetching container bookings:", error);
      } finally {
        setLoadingContainerBookings(false);
      }
    };

    fetchExistingBookings();
  }, [selectedContainerId, selectedBookingIds, bulkActionDrawerVisible]);

  // Table columns for bookings table
  const columns: ColumnsType<Booking> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 90,
      sorter: true,
      render: (id, record) => {
        // Get formatted reference
        const reference = getFormattedBookingReference(record);

        // Split the reference to isolate the numeric ID part
        // Format is typically EFF<numeric_id><transport_abbreviation>
        // Extract the components
        const prefix = "EFF";
        const numericId = String(id);
        const suffix = reference.substring(prefix.length + numericId.length);

        // Check if this booking has pending tasks
        const hasPendingTasks = bookingsWithPendingTasks.has(id);

        return (
          <div>
            <div>
              {hasPendingTasks && (
                <Tooltip title="Has pending tasks">
                  <Badge
                    status="processing"
                    color="orange"
                    style={{ marginRight: 4 }}
                  />
                </Tooltip>
              )}
              <span style={{ fontSize: "11px" }}>{prefix}</span>
              <span style={{ fontWeight: 700, fontSize: "14px" }}>
                {numericId}
              </span>
              <span style={{ fontSize: "11px" }}>{suffix}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      sorter: true,
      width: 150,
      render: (reference) =>
        reference ? (
          <Tooltip title={reference}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 130,
              }}
            >
              {reference}
            </div>
          </Tooltip>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Company",
      key: "company",
      width: 180,
      render: (_, record) => {
        // Handle both object and array formats
        const companyInfo = record.company_info;
        let companyName = "N/A";

        if (companyInfo) {
          if (Array.isArray(companyInfo) && companyInfo.length > 0) {
            companyName = companyInfo[0].name || "N/A";
          } else if (typeof companyInfo === "object") {
            companyName = (companyInfo as Company).name || "N/A";
          }
        }

        return (
          <Tooltip title={companyName}>
            <span>{companyName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      sorter: true,
      render: (status) => {
        const color = getStatusColor(status);
        let displayText = status || "unknown";

        // Make first letter uppercase
        displayText =
          displayText.charAt(0).toUpperCase() + displayText.slice(1);

        // Special case for 'submitted' status
        if (status === BookingStatus.SUBMITTED) {
          return <Tag color={color}>Pending Approval</Tag>;
        }

        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: "Transport",
      key: "transport_type",
      width: 80,
      render: (_, record) => {
        const transportInfo = record.transport_type_info;
        let transportName = "N/A";
        let transportAbbr = "";

        if (transportInfo) {
          if (Array.isArray(transportInfo) && transportInfo.length > 0) {
            transportName = transportInfo[0].name || "N/A";
            transportAbbr = transportInfo[0].abbreviation || "";
          } else if (typeof transportInfo === "object") {
            transportName = (transportInfo as TransportType).name || "N/A";
            transportAbbr = (transportInfo as TransportType).abbreviation || "";
          }
        }

        // Display emoji based on transport type
        let emoji = "";
        if (transportAbbr === "AIR") {
          emoji = "✈️";
        } else if (transportAbbr === "SEA") {
          emoji = "🚢";
        }

        return (
          <Tooltip title={transportName}>
            <span
              style={{
                fontSize: "18px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {emoji || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Booking Type",
      key: "booking_type",
      width: 130,
      render: (_, record) => {
        const bookingTypeInfo = record.booking_type_info;
        let bookingTypeName = "-";

        if (bookingTypeInfo) {
          if (Array.isArray(bookingTypeInfo) && bookingTypeInfo.length > 0) {
            bookingTypeName = bookingTypeInfo[0].name || "-";
          } else if (typeof bookingTypeInfo === "object") {
            bookingTypeName = (bookingTypeInfo as BookingType).name || "-";
          }
        }

        return (
          <Tooltip title={bookingTypeName}>
            <span>{bookingTypeName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "🪣 Origin",
      key: "origin",
      width: 150,
      render: (_, record) => {
        // Get origin info
        const originInfo = record.origin_port_info;
        let originName = "-";

        if (originInfo) {
          if (Array.isArray(originInfo) && originInfo.length > 0) {
            originName = originInfo[0].name || "-";
          } else if (typeof originInfo === "object") {
            originName = (originInfo as Port).name || "-";
          }
        }

        // Check for container origin
        const containerInfo = record.container_info;
        let containerOrigin = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.container_origin_port) {
            const containerOriginPort = Array.isArray(
              container.container_origin_port
            )
              ? container.container_origin_port[0]
              : container.container_origin_port;

            if (containerOriginPort && containerOriginPort.name) {
              containerOrigin = containerOriginPort.name;
            }
          }
        }

        // If there's container origin, display it
        if (containerOrigin) {
          return <span>{containerOrigin}</span>;
        }

        return <span>{originName}</span>;
      },
    },
    {
      title: "🪣 Destination",
      key: "destination",
      width: 150,
      render: (_, record) => {
        // Get destination info
        const destInfo = record.destination_port_info;
        let destName = "-";

        if (destInfo) {
          if (Array.isArray(destInfo) && destInfo.length > 0) {
            destName = destInfo[0].name || "-";
          } else if (typeof destInfo === "object") {
            destName = (destInfo as Port).name || "-";
          }
        }

        // Check for container destination
        const containerInfo = record.container_info;
        let containerDest = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.container_destination_port) {
            const containerDestPort = Array.isArray(
              container.container_destination_port
            )
              ? container.container_destination_port[0]
              : container.container_destination_port;

            if (containerDestPort && containerDestPort.name) {
              containerDest = containerDestPort.name;
            }
          }
        }

        // If there's container destination, display it
        if (containerDest) {
          return <span>{containerDest}</span>;
        }

        return <span>{destName}</span>;
      },
    },
    {
      title: "🪣 ETD",
      key: "etd",
      width: 120,
      render: (_, record) => {
        // Check for container ETD
        const containerInfo = record.container_info;
        let containerEtd = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.etd) {
            containerEtd = formatDate(container.etd);
          }
        }

        // If there's container ETD, display it
        if (containerEtd) {
          return <span>{containerEtd}</span>;
        }

        return <span>-</span>;
      },
    },
    {
      title: "🪣 ETA",
      dataIndex: "eta",
      key: "eta",
      sorter: true,
      width: 120,
      render: (date, record) => {
        // Check for container ETA
        const containerInfo = record.container_info;
        let containerEta = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.eta) {
            containerEta = formatDate(container.eta);
          }
        }

        // If there's container ETA, display it
        if (containerEta) {
          return <span>{containerEta}</span>;
        }

        // Format booking ETA
        return date ? formatDate(date) : "-";
      },
    },
    {
      title: "Container",
      key: "container",
      width: 120,
      render: (_, record) => {
        // Get container info
        const containerInfo = record.container_info;
        let containerReference = "-";
        let containerId = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container) {
            containerId = container.id;
            if (container.reference) {
              containerReference = container.reference;
            } else if (container.id) {
              containerReference = `#${container.id}`;
            }
          }
        }

        // If there's no container, return a simple dash
        if (containerReference === "-" || !containerId) {
          return <span>{containerReference}</span>;
        }

        // Otherwise, render a clickable link
        return (
          <Tooltip title="View container details">
            <a
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering row click (booking details)
                window.open(`/admin/containers/${containerId}`, "_blank");
              }}
              style={{ cursor: "pointer", color: "#1890ff" }}
            >
              {containerReference}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: "⭐ Master Container",
      key: "master_container",
      width: 140,
      render: (_, record) => {
        // Get container info first
        const containerInfo = record.container_info;
        let masterContainerInfo = null;

        if (containerInfo) {
          const container = Array.isArray(containerInfo)
            ? containerInfo[0]
            : containerInfo;

          if (container && container.master_container_info) {
            const masterContainerData = Array.isArray(
              container.master_container_info
            )
              ? container.master_container_info[0]
              : container.master_container_info;

            if (masterContainerData && masterContainerData.master_containers) {
              const masterContainer = Array.isArray(
                masterContainerData.master_containers
              )
                ? masterContainerData.master_containers[0]
                : masterContainerData.master_containers;

              if (masterContainer) {
                masterContainerInfo = {
                  id: masterContainer.id,
                  reference: masterContainer.reference,
                };
              }
            }
          }
        }

        // If there's no master container, return a dash
        if (!masterContainerInfo) {
          return <span>-</span>;
        }

        // Otherwise, render a clickable link
        return (
          <Tooltip title="View master container">
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering row click (booking details)
                window.open(
                  `/admin/containers?tab=master-containers&master_id=${masterContainerInfo.id}`,
                  "_blank"
                );
              }}
              style={{ padding: 0, height: "auto" }}
            >
              {masterContainerInfo.reference || `#${masterContainerInfo.id}`}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Products",
      key: "products",
      width: 90,
      render: (_, record) => {
        const productInfo = record.product_info;
        let productCount = 0;

        if (productInfo) {
          if (Array.isArray(productInfo)) {
            productCount = productInfo.length;
          }
        }

        return <span>{productCount > 0 ? productCount : "-"}</span>;
      },
    },
    {
      title: "Delivery",
      key: "delivery",
      width: 100,
      render: (_, record) => {
        // Get delivery info
        const deliveryInfo = record.booking_delivery_info;

        // Debug: Let's see what we're getting
        console.log(`Booking ${record.id} delivery info:`, deliveryInfo);

        // Check if delivery info exists and has valid data
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));

        console.log(`Booking ${record.id} hasDelivery:`, hasDelivery);

        // If no delivery record exists, show storage icon
        if (!hasDelivery) {
          return (
            <Tooltip title="In opslag (storage)">
              <InboxOutlined
                style={{
                  color: "#666",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        }

        // Get the delivery data
        const deliveryData = Array.isArray(deliveryInfo)
          ? deliveryInfo[0]
          : deliveryInfo;

        // Format full address
        const formatAddress = (delivery: any) => {
          const parts = [];
          if (delivery.street_name) parts.push(delivery.street_name);
          if (delivery.house_number) parts.push(delivery.house_number);
          if (delivery.city) parts.push(delivery.city);
          if (delivery.postal_code) parts.push(delivery.postal_code);
          if (delivery.country) parts.push(delivery.country);
          return parts.join(", ");
        };

        const fullAddress = formatAddress(deliveryData);

        return (
          <Tooltip
            title={fullAddress || "Delivery address"}
            placement="topLeft"
          >
            <HomeOutlined
              style={{
                color: "#1890ff",
                fontSize: "16px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Agent",
      key: "agent",
      width: 140,
      render: (_, record) => {
        // Handle both object and array formats
        const agentInfo = record.agent_info;
        let agentName = "";

        if (agentInfo) {
          if (Array.isArray(agentInfo) && agentInfo.length > 0) {
            agentName = agentInfo[0].name || "";
          } else if (typeof agentInfo === "object") {
            agentName = (agentInfo as Agent).name || "";
          }
        }

        return <span>{agentName || "-"}</span>;
      },
    },
    {
      title: "📄 Invoice",
      key: "invoice",
      width: 120,
      render: (_, record) => {
        // Handle invoice info
        const invoiceInfo = record.invoice_info;
        let hasInvoice = false;
        let invoiceData = null;

        if (invoiceInfo) {
          if (Array.isArray(invoiceInfo) && invoiceInfo.length > 0) {
            hasInvoice = true;
            invoiceData = invoiceInfo[0];
          } else if (typeof invoiceInfo === "object") {
            hasInvoice = true;
            invoiceData = invoiceInfo;
          }
        }

        if (!hasInvoice) {
          return (
            <Button
              type="link"
              size="small"
              style={{
                padding: 0,
                height: "auto",
                color: "#1890ff",
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to invoices tab with booking ID for creation
                navigate(`/admin/invoices?create_from_booking=${record.id}`);
              }}
            >
              Create Invoice
            </Button>
          );
        }

        // Get payment status color
        const getPaymentStatusColor = (status: string | null | undefined) => {
          switch (status?.toLowerCase()) {
            case "paid":
              return "#52c41a"; // green
            case "open":
              return "#faad14"; // orange
            case "overdue":
              return "#ff4d4f"; // red
            case "partial":
              return "#1890ff"; // blue
            default:
              return "#d9d9d9"; // gray
          }
        };

        const paymentStatus =
          (invoiceData as InvoiceInfo)?.moneybird_payment_status || "unknown";
        const statusColor = getPaymentStatusColor(paymentStatus);

        // Calculate total amount from invoice lines
        const calculateTotal = (lines: InvoiceLine[]) => {
          if (!lines || !Array.isArray(lines)) return 0;
          return lines.reduce((total, line) => {
            const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
            const vatAmount = lineTotal * ((line.vat_percentage || 0) / 100);
            return total + lineTotal + vatAmount;
          }, 0);
        };

        const totalAmount = calculateTotal(
          (invoiceData as InvoiceInfo)?.lines || []
        );

        const invoice = invoiceData as InvoiceInfo;
        const lines = invoice?.lines || [];

        // Create detailed tooltip content
        const tooltipContent = (
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>
              Invoice #{invoice?.id}
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Total:</strong> {invoice?.currency}{" "}
              {totalAmount.toFixed(2)}
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Status:</strong>{" "}
              <span style={{ color: statusColor, fontWeight: "bold" }}>
                {paymentStatus}
              </span>
            </div>

            <div style={{ marginBottom: 4 }}>
              <strong>Created:</strong>{" "}
              {invoice?.created_at
                ? new Date(invoice.created_at).toLocaleDateString()
                : "N/A"}
            </div>

            {invoice?.moneybird_invoice_date && (
              <div style={{ marginBottom: 4 }}>
                <strong>Invoice Date:</strong>{" "}
                {new Date(invoice.moneybird_invoice_date).toLocaleDateString()}
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <strong>Lines:</strong> {lines.length} item
              {lines.length !== 1 ? "s" : ""}
            </div>

            {lines.length > 0 && (
              <div style={{ fontSize: "11px", color: "#666" }}>
                <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                  Breakdown:
                </div>
                {lines.slice(0, 3).map((line, index) => {
                  const lineTotal =
                    (line.quantity || 0) * (line.unit_price || 0);
                  const vatAmount =
                    lineTotal * ((line.vat_percentage || 0) / 100);
                  const lineTotalWithVat = lineTotal + vatAmount;

                  return (
                    <div key={index} style={{ marginBottom: 2 }}>
                      {line.quantity}x @ {invoice?.currency}{" "}
                      {(line.unit_price || 0).toFixed(2)} = {invoice?.currency}{" "}
                      {lineTotalWithVat.toFixed(2)}
                      {line.vat_percentage > 0 && (
                        <span style={{ color: "#888" }}>
                          {" "}
                          (inc. {line.vat_percentage}% VAT)
                        </span>
                      )}
                    </div>
                  );
                })}
                {lines.length > 3 && (
                  <div style={{ color: "#888", fontStyle: "italic" }}>
                    ... and {lines.length - 3} more line
                    {lines.length - 3 !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: "11px", color: "#666" }}>
              Click to view in Invoices tab
            </div>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            overlayStyle={{ maxWidth: 350 }}
            trigger="hover"
            mouseEnterDelay={0.3}
            mouseLeaveDelay={0.1}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to invoices tab and show this specific invoice
                navigate(`/admin/invoices?invoice_id=${invoice?.id}`);
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: statusColor,
                }}
              />
              <span>#{invoice?.id}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Generate & Download Packing List">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                handleDownloadPackingSlip(record);
              }}
            />
          </Tooltip>
          {record.status === BookingStatus.SUBMITTED && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  style={{ color: "#52c41a" }}
                  icon={<CheckCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    handleApproveBooking(record);
                  }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    Modal.confirm({
                      title: "Reject Booking",
                      content: (
                        <div>
                          <p>Are you sure you want to reject this booking?</p>
                          <AntInput.TextArea
                            placeholder="Reason for rejection"
                            rows={3}
                            id="rejection-reason"
                          />
                        </div>
                      ),
                      onOk: () => {
                        const reasonElement = document.getElementById(
                          "rejection-reason"
                        ) as HTMLTextAreaElement;
                        const reason = reasonElement?.value || "";
                        handleRejectBooking(record, reason);
                      },
                    });
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Render filtering controls
  const renderFilters = () => {
    return (
      <div style={{ marginBottom: 8 }}>
        {/* First row with all primary filters in a compact layout */}
        <Row gutter={[8, 8]} align="middle">
          <Col span={5}>
            <Input
              placeholder="Search ID or reference..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              size="middle"
            />
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              allowClear
              size="middle"
            >
              <Option value="all">All Statuses</Option>
              <Option value={BookingStatus.WAITING_FOR_GOODS}>
                {bookingStatusLabels[BookingStatus.WAITING_FOR_GOODS]}
              </Option>
              <Option value={BookingStatus.CANCELLED}>
                {bookingStatusLabels[BookingStatus.CANCELLED]}
              </Option>
              <Option value={BookingStatus.OUT_FOR_DELIVERY}>
                {bookingStatusLabels[BookingStatus.OUT_FOR_DELIVERY]}
              </Option>
              <Option value={BookingStatus.CONCEPT}>
                {bookingStatusLabels[BookingStatus.CONCEPT]}
              </Option>
              <Option value={BookingStatus.ADDED_TO_STOCK}>
                {bookingStatusLabels[BookingStatus.ADDED_TO_STOCK]}
              </Option>
              <Option value={BookingStatus.GOODS_RECEIVED_ORIGIN}>
                {bookingStatusLabels[BookingStatus.GOODS_RECEIVED_ORIGIN]}
              </Option>
              <Option value={BookingStatus.APPROVED}>
                {bookingStatusLabels[BookingStatus.APPROVED]}
              </Option>
              <Option value={BookingStatus.IN_TRANSIT}>
                {bookingStatusLabels[BookingStatus.IN_TRANSIT]}
              </Option>
              <Option value={BookingStatus.CUSTOM_CLEARANCE}>
                {bookingStatusLabels[BookingStatus.CUSTOM_CLEARANCE]}
              </Option>
              <Option value={BookingStatus.ARCHIVED}>
                {bookingStatusLabels[BookingStatus.ARCHIVED]}
              </Option>
              <Option value={BookingStatus.REJECTED}>
                {bookingStatusLabels[BookingStatus.REJECTED]}
              </Option>
              <Option value={BookingStatus.DELIVERED}>
                {bookingStatusLabels[BookingStatus.DELIVERED]}
              </Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Company"
              value={companyFilter}
              onChange={handleCompanyFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id.toString()}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Transport"
              value={transportTypeFilter}
              onChange={handleTransportTypeFilterChange}
              allowClear
              size="middle"
            >
              {transportTypes.map((type) => (
                <Option key={type.id} value={type.id.toString()}>
                  {type.abbreviation === "AIR"
                    ? "✈️"
                    : type.abbreviation === "SEA"
                    ? "🚢"
                    : ""}{" "}
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Booking Type"
              value={bookingTypeFilter}
              onChange={handleBookingTypeFilterChange}
              allowClear
              size="middle"
              showSearch
              optionFilterProp="children"
            >
              {bookingTypes.map((type) => (
                <Option key={type.id} value={type.id.toString()}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={handleDateRangeChange}
              placeholder={["Start", "End"]}
              size="middle"
            />
          </Col>
          <Col span={3} style={{ textAlign: "right" }}>
            <Space>
              {selectedBookingIds.length > 0 && (
                <Button
                  type="primary"
                  icon={<ContainerOutlined />}
                  onClick={() => setBulkActionDrawerVisible(true)}
                  size="middle"
                >
                  Bulk ({selectedBookingIds.length})
                </Button>
              )}
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadBookings();
                  onRefresh();
                }}
                loading={loading}
                size="middle"
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Second row with additional filters */}
        <Row gutter={[8, 0]} align="middle" style={{ marginTop: 8 }}>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Agent"
              value={agentFilter}
              onChange={handleAgentFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {agents.map((agent) => (
                <Option key={agent.id} value={agent.id.toString()}>
                  {agent.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="Consolidation"
              value={
                consolidationFilter === null
                  ? undefined
                  : consolidationFilter.toString()
              }
              onChange={(value) =>
                handleConsolidationFilterChange(
                  value === undefined ? null : value === "true"
                )
              }
              allowClear
              size="middle"
            >
              <Option value="true">Consolidated</Option>
              <Option value="false">Not Consolidated</Option>
            </Select>
          </Col>
          <Col span={5}>
            <Select
              style={{ width: "100%" }}
              placeholder="Container"
              value={containerFilter}
              onChange={handleContainerFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {containers.map((container) => {
                // Get container origin and destination
                const originPort = container.container_origin_port
                  ? Array.isArray(container.container_origin_port)
                    ? container.container_origin_port[0]?.name
                    : container.container_origin_port.name
                  : null;

                const destPort = container.container_destination_port
                  ? Array.isArray(container.container_destination_port)
                    ? container.container_destination_port[0]?.name
                    : container.container_destination_port.name
                  : null;

                // Format display text
                const displayText = `${container.reference || container.id} ${
                  originPort ? `(${originPort}` : ""
                }${destPort ? ` → ${destPort})` : originPort ? ")" : ""}`;

                return (
                  <Option key={container.id} value={container.id.toString()}>
                    🚢 {displayText}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="⭐ Master Container"
              value={masterContainerFilter}
              onChange={handleMasterContainerFilterChange}
              allowClear
              showSearch
              optionFilterProp="children"
              size="middle"
            >
              {masterContainers.map((masterContainer) => (
                <Option
                  key={masterContainer.id}
                  value={masterContainer.id.toString()}
                >
                  ⭐ {masterContainer.reference || `#${masterContainer.id}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="📄 Invoice"
              value={invoiceFilter}
              onChange={handleInvoiceFilterChange}
              allowClear
              size="middle"
            >
              <Option value="with_invoice">With Invoice</Option>
              <Option value="no_invoice">No Invoice</Option>
            </Select>
          </Col>
          <Col span={3}>
            <Select
              style={{ width: "100%" }}
              placeholder="🏠 Delivery"
              value={deliveryFilter}
              onChange={handleDeliveryFilterChange}
              allowClear
              size="middle"
            >
              <Option value="delivery">For Delivery</Option>
              <Option value="storage">In Storage</Option>
            </Select>
          </Col>
        </Row>
      </div>
    );
  };

  // Render bulk action drawer
  const renderBulkActionDrawer = () => {
    const selectedBookings = bookings.filter((booking) =>
      selectedBookingIds.includes(booking.id)
    );

    // Calculate totals for selected bookings
    const selectedBookingsTotals = calculateBookingsTotals(selectedBookings);

    // Calculate totals for existing bookings in container
    const existingBookingsTotals = calculateBookingsTotals(
      existingContainerBookings
    );

    return (
      <Drawer
        title={`Bulk Actions (${selectedBookingIds.length} bookings selected)`}
        width={500}
        onClose={() => setBulkActionDrawerVisible(false)}
        open={bulkActionDrawerVisible}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button
              onClick={() => setBulkActionDrawerVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setBulkStatusChangeVisible(true)}
              style={{ marginRight: 8 }}
              disabled={selectedBookingIds.length === 0}
            >
              Change Status
            </Button>
            <Button
              type="primary"
              onClick={handleAddToContainer}
              loading={processingBulkAction}
              disabled={!selectedContainerId || selectedBookingIds.length === 0}
            >
              Add to Container
            </Button>
          </div>
        }
      >
        <Alert
          message="Add multiple bookings to a container"
          description="Select a container below to add all selected bookings to it."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Divider orientation="left">Select Container</Divider>
        <Select
          style={{ width: "100%" }}
          placeholder="Select Container"
          value={selectedContainerId}
          onChange={setSelectedContainerId}
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {containers.map((container) => {
            // Get container origin and destination
            const originPort = container.container_origin_port
              ? Array.isArray(container.container_origin_port)
                ? container.container_origin_port[0]?.name
                : container.container_origin_port.name
              : null;

            const destPort = container.container_destination_port
              ? Array.isArray(container.container_destination_port)
                ? container.container_destination_port[0]?.name
                : container.container_destination_port.name
              : null;

            // Format display text
            const displayText = `${container.reference || container.id} ${
              originPort ? `(${originPort}` : ""
            }${destPort ? ` → ${destPort})` : originPort ? ")" : ""}`;

            return (
              <Option key={container.id} value={container.id.toString()}>
                🚢 {displayText}
              </Option>
            );
          })}
        </Select>

        <Divider orientation="left">Selected Bookings</Divider>

        {/* Summary for selected bookings */}
        <div
          style={{
            display: "flex",
            marginBottom: 8,
            background: "#f5f5f5",
            padding: "8px",
            borderRadius: "4px",
          }}
        >
          <div
            style={{
              flex: 1,
              textAlign: "center",
              borderRight: "1px solid #e8e8e8",
            }}
          >
            <div style={{ fontSize: "11px", color: "#888" }}>Total Weight</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalWeight.toFixed(2)} kg
            </div>
          </div>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              borderRight: "1px solid #e8e8e8",
            }}
          >
            <div style={{ fontSize: "11px", color: "#888" }}>Total Cartons</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalCartons}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: "11px", color: "#888" }}>Total CBM</div>
            <div style={{ fontWeight: "bold" }}>
              {selectedBookingsTotals.totalCBM.toFixed(2)} m³
            </div>
          </div>
        </div>

        <List
          size="small"
          bordered
          dataSource={selectedBookings}
          renderItem={(item) => {
            const reference = getFormattedBookingReference(item);
            // Get company name
            const company = Array.isArray(item.company_info)
              ? item.company_info[0]?.name
              : item.company_info?.name || "Unknown";

            // Get status with color
            const statusColor = getStatusColor(item.status || "");
            const status = item.status
              ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
              : "Unknown";

            // Get transport type
            const transportInfo = item.transport_type_info;
            let transportEmoji = "";
            let transportName = "";

            if (transportInfo) {
              if (Array.isArray(transportInfo) && transportInfo.length > 0) {
                transportName = transportInfo[0].abbreviation || "";
                transportEmoji =
                  transportInfo[0].abbreviation === "AIR"
                    ? "✈️"
                    : transportInfo[0].abbreviation === "SEA"
                    ? "🚢"
                    : "";
              } else if (typeof transportInfo === "object") {
                const typedTransportInfo = transportInfo as TransportType;
                transportName = typedTransportInfo.abbreviation || "";
                transportEmoji =
                  typedTransportInfo.abbreviation === "AIR"
                    ? "✈️"
                    : typedTransportInfo.abbreviation === "SEA"
                    ? "🚢"
                    : "";
              }
            }

            // Get booking type
            const bookingTypeInfo = item.booking_type_info;
            let bookingTypeName = "";

            if (bookingTypeInfo) {
              if (
                Array.isArray(bookingTypeInfo) &&
                bookingTypeInfo.length > 0
              ) {
                bookingTypeName = bookingTypeInfo[0].name || "";
              } else if (typeof bookingTypeInfo === "object") {
                const typedBookingTypeInfo = bookingTypeInfo as BookingType;
                bookingTypeName = typedBookingTypeInfo.name || "";
              }
            }

            // Get product summary for this booking
            const productSummary = getBookingProductSummary(item);

            return (
              <List.Item
                style={{ padding: "4px 8px" }}
                actions={[
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      // Remove this booking from selection
                      setSelectedBookingIds((prevIds) =>
                        prevIds.filter((id) => id !== item.id)
                      );
                    }}
                  >
                    Remove
                  </Button>,
                ]}
              >
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: "1 1 auto" }}>
                      <strong>{reference}</strong> - {company}
                    </div>
                    <div
                      style={{ display: "flex", gap: "4px", fontSize: "12px" }}
                    >
                      {item.status && (
                        <Tag color={statusColor} style={{ margin: 0 }}>
                          {status}
                        </Tag>
                      )}
                      {transportName && (
                        <Tag color="blue" style={{ margin: 0 }}>
                          {transportEmoji} {transportName}
                        </Tag>
                      )}
                      {bookingTypeName && (
                        <Tag color="purple" style={{ margin: 0 }}>
                          {bookingTypeName}
                        </Tag>
                      )}
                    </div>
                  </div>

                  {/* Add product summary line */}
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginTop: "2px",
                    }}
                  >
                    {productSummary.totalWeight > 0 &&
                      `${productSummary.totalWeight.toFixed(2)} kg · `}
                    {productSummary.totalCartons > 0 &&
                      `${productSummary.totalCartons} cartons · `}
                    {productSummary.totalCBM > 0 &&
                      `${productSummary.totalCBM.toFixed(2)} m³`}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />

        {selectedContainerId && (
          <>
            <Divider orientation="left">
              Existing Bookings in Container{" "}
              {loadingContainerBookings && <small>(Loading...)</small>}
            </Divider>

            {/* Summary for existing bookings */}
            {existingContainerBookings.length > 0 && (
              <div
                style={{
                  display: "flex",
                  marginBottom: 8,
                  background: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    borderRight: "1px solid #e8e8e8",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total Weight
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalWeight.toFixed(2)} kg
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    borderRight: "1px solid #e8e8e8",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total Cartons
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalCartons}
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: "11px", color: "#888" }}>
                    Total CBM
                  </div>
                  <div style={{ fontWeight: "bold" }}>
                    {existingBookingsTotals.totalCBM.toFixed(2)} m³
                  </div>
                </div>
              </div>
            )}

            <List
              size="small"
              bordered
              loading={loadingContainerBookings}
              dataSource={existingContainerBookings}
              locale={{ emptyText: "No existing bookings in this container" }}
              renderItem={(item) => {
                const reference = getFormattedBookingReference(item);
                const company = Array.isArray(item.company_info)
                  ? item.company_info[0]?.name
                  : item.company_info?.name || "Unknown";

                // Get status with color
                const statusColor = getStatusColor(item.status || "");
                const status = item.status
                  ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                  : "Unknown";

                // Get transport type
                const transportInfo = item.transport_type_info;
                let transportEmoji = "";
                let transportName = "";

                if (transportInfo) {
                  if (
                    Array.isArray(transportInfo) &&
                    transportInfo.length > 0
                  ) {
                    transportName = transportInfo[0].abbreviation || "";
                    transportEmoji =
                      transportInfo[0].abbreviation === "AIR"
                        ? "✈️"
                        : transportInfo[0].abbreviation === "SEA"
                        ? "🚢"
                        : "";
                  } else if (typeof transportInfo === "object") {
                    const typedTransportInfo = transportInfo as TransportType;
                    transportName = typedTransportInfo.abbreviation || "";
                    transportEmoji =
                      typedTransportInfo.abbreviation === "AIR"
                        ? "✈️"
                        : typedTransportInfo.abbreviation === "SEA"
                        ? "🚢"
                        : "";
                  }
                }

                // Get booking type
                const bookingTypeInfo = item.booking_type_info;
                let bookingTypeName = "";

                if (bookingTypeInfo) {
                  if (
                    Array.isArray(bookingTypeInfo) &&
                    bookingTypeInfo.length > 0
                  ) {
                    bookingTypeName = bookingTypeInfo[0].name || "";
                  } else if (typeof bookingTypeInfo === "object") {
                    const typedBookingTypeInfo = bookingTypeInfo as BookingType;
                    bookingTypeName = typedBookingTypeInfo.name || "";
                  }
                }

                // Get product summary for this booking
                const productSummary = getBookingProductSummary(item);

                return (
                  <List.Item style={{ padding: "4px 8px" }}>
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: "1 1 auto", fontSize: "12px" }}>
                          <strong>{reference}</strong> - {company}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {item.status && (
                            <Tag color={statusColor} style={{ margin: 0 }}>
                              {status}
                            </Tag>
                          )}
                          {transportName && (
                            <Tag color="blue" style={{ margin: 0 }}>
                              {transportEmoji} {transportName}
                            </Tag>
                          )}
                          {bookingTypeName && (
                            <Tag color="purple" style={{ margin: 0 }}>
                              {bookingTypeName}
                            </Tag>
                          )}
                        </div>
                      </div>

                      {/* Add product summary line */}
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          marginTop: "2px",
                        }}
                      >
                        {productSummary.totalWeight > 0 &&
                          `${productSummary.totalWeight.toFixed(2)} kg · `}
                        {productSummary.totalCartons > 0 &&
                          `${productSummary.totalCartons} cartons · `}
                        {productSummary.totalCBM > 0 &&
                          `${productSummary.totalCBM.toFixed(2)} m³`}
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />

            {/* Show combined totals if both selected and existing bookings exist */}
            {selectedBookings.length > 0 &&
              existingContainerBookings.length > 0 && (
                <>
                  <Divider orientation="left">Combined Totals</Divider>
                  <div
                    style={{
                      display: "flex",
                      background: "#e6f7ff",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #91d5ff",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        textAlign: "center",
                        borderRight: "1px solid #91d5ff",
                      }}
                    >
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total Weight
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {(
                          selectedBookingsTotals.totalWeight +
                          existingBookingsTotals.totalWeight
                        ).toFixed(2)}{" "}
                        kg
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        textAlign: "center",
                        borderRight: "1px solid #91d5ff",
                      }}
                    >
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total Cartons
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {selectedBookingsTotals.totalCartons +
                          existingBookingsTotals.totalCartons}
                      </div>
                    </div>
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: "11px", color: "#0050b3" }}>
                        Total CBM
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {(
                          selectedBookingsTotals.totalCBM +
                          existingBookingsTotals.totalCBM
                        ).toFixed(2)}{" "}
                        m³
                      </div>
                    </div>
                  </div>
                </>
              )}
          </>
        )}
      </Drawer>
    );
  };

  // Helper function to calculate totals for a list of bookings
  const calculateBookingsTotals = (bookingsList: Booking[]) => {
    let totalWeight = 0;
    let totalCartons = 0;
    let totalCBM = 0;

    bookingsList.forEach((booking) => {
      const productInfo = booking.product_info;

      if (productInfo) {
        if (Array.isArray(productInfo)) {
          productInfo.forEach((product) => {
            // Use ?? 0 to handle null/undefined values
            totalWeight += product.gross_weight ?? 0;
            totalCartons += product.cartons ?? 0;
            totalCBM += product.cbm ?? 0;
          });
        }
      }
    });

    return { totalWeight, totalCartons, totalCBM };
  };

  // Helper function to get product summary for a single booking
  const getBookingProductSummary = (booking: Booking) => {
    let totalWeight = 0;
    let totalCartons = 0;
    let totalCBM = 0;

    const productInfo = booking.product_info;

    if (productInfo) {
      if (Array.isArray(productInfo)) {
        productInfo.forEach((product) => {
          // Use ?? 0 to handle null/undefined values
          totalWeight += product.gross_weight ?? 0;
          totalCartons += product.cartons ?? 0;
          totalCBM += product.cbm ?? 0;
        });
      }
    }

    return { totalWeight, totalCartons, totalCBM };
  };

  return (
    <>
      {renderFilters()}

      <CompactTable>
        <Table
          className="compact-table"
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          columns={columns}
          dataSource={bookings}
          rowKey="id"
          pagination={
            isPendingList
              ? { pageSize: 10, hideOnSinglePage: true }
              : {
                  ...pagination,
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} bookings`,
                }
          }
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          onRow={(record) => ({
            onClick: (event) => {
              // Don't open booking details if we're processing this booking
              // or if we clicked on a checkbox
              const target = event.target as HTMLElement;
              const isCheckbox =
                target.tagName === "INPUT" &&
                target.getAttribute("type") === "checkbox";

              if (!processingBookingIds.includes(record.id) && !isCheckbox) {
                onViewDetails(record);
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </CompactTable>

      {renderBulkActionDrawer()}

      {/* Bulk Status Change Modal */}
      <Modal
        title={`Change Status (${selectedBookingIds.length} bookings selected)`}
        open={bulkStatusChangeVisible}
        onOk={handleBulkStatusChange}
        onCancel={() => {
          setBulkStatusChangeVisible(false);
          setSelectedBulkStatus(null);
        }}
        confirmLoading={processingBulkStatus}
        okText="Update Status"
        okButtonProps={{ disabled: !selectedBulkStatus }}
      >
        <Alert
          message="Bulk Status Change"
          description="Select a new status to apply to all selected bookings."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <strong>Select New Status:</strong>
        </div>

        <Select
          style={{ width: "100%" }}
          placeholder="Select a status"
          value={selectedBulkStatus}
          onChange={setSelectedBulkStatus}
          showSearch
          optionFilterProp="children"
        >
          {Object.entries(bookingStatusLabels).map(([value, label]) => (
            <Option key={value} value={value}>
              <Tag color={getStatusColor(value)} style={{ marginRight: 8 }}>
                {label}
              </Tag>
              {label}
            </Option>
          ))}
        </Select>

        <div style={{ marginTop: 16, fontSize: 12, color: "#666" }}>
          This will update the status for all {selectedBookingIds.length}{" "}
          selected booking(s).
        </div>
      </Modal>
    </>
  );
};

export default BookingsList;
